/**
 * ResumeEditorPage -- Edit resume fields inside collapsible panels.
 *
 * Uses useResumeStore (ADR-021), Panel compound component (ADR-022 Pattern 1).
 * Gracefully handles 404 since the API endpoint may not exist yet.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useResumeStore, useResumeActions } from '../state/useResumeStore';
import { Panel } from '../components/ui/Panel';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { MonthYearPicker } from '../components/ui/MonthYearPicker';
import { Button } from '../components/ui/Button';
import { LoadingState } from '../components/ui/LoadingState';
import { useSaveHandler } from '../hooks/useSaveHandler';
import { logger } from '../services/logger.client';
import type { Resume, WorkExperience } from '../types';

const log = logger.child({ module: 'ResumeEditorPage' });

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function emptyExperience(): WorkExperience {
  return {
    id: generateId(),
    company: '',
    title: '',
    startDate: '',
    endDate: '',
    description: '',
  };
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export function ResumeEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const resumes = useResumeStore((s) => s.resumes);
  const isLoading = useResumeStore((s) => s.isLoading);
  const storeError = useResumeStore((s) => s.error);
  const hasFetched = useResumeStore((s) => s.hasFetched);
  const { fetchResumes, updateResume, deleteResume } = useResumeActions();

  /* -------- save feedback -------- */
  const { isSaving, saveMessage, saveError, execute: executeSave } = useSaveHandler({
    getStoreError: () => useResumeStore.getState().error,
  });

  /* -------- local draft state -------- */
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [skills, setSkills] = useState('');
  const [workExperience, setWorkExperience] = useState<WorkExperience[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  /* -------- load resume into local state -------- */
  const resume: Resume | undefined = resumes.find((r) => r.id === id);

  useEffect(() => {
    if (!hasFetched) {
      log.info('No resumes in store -- fetching');
      void fetchResumes();
    }
  }, [hasFetched, fetchResumes]);

  useEffect(() => {
    if (resume) {
      log.debug('Populating editor fields from resume', { id: resume.id });
      setTitle(resume.title);
      setSummary(resume.summary);
      setSkills(resume.skills.join(', '));
      setWorkExperience(resume.workExperience);
    }
  }, [resume]);

  /* -------- work experience helpers -------- */
  const addExperience = useCallback(() => {
    setWorkExperience((prev) => [...prev, emptyExperience()]);
  }, []);

  const removeExperience = useCallback((expId: string) => {
    setWorkExperience((prev) => prev.filter((w) => w.id !== expId));
  }, []);

  const updateExperience = useCallback(
    (expId: string, field: keyof Omit<WorkExperience, 'id'>, value: string) => {
      setWorkExperience((prev) =>
        prev.map((w) => (w.id === expId ? { ...w, [field]: value } : w)),
      );
    },
    [],
  );

  /* -------- save -------- */
  async function handleSave() {
    if (!id) return;
    log.info('Saving resume', { id });
    await executeSave(() =>
      updateResume(id, {
        title,
        summary,
        skills: skills
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        workExperience,
      }),
    );
  }

  /* -------- delete -------- */
  async function handleDelete() {
    if (!id) return;
    if (!window.confirm('Delete this resume? This cannot be undone.')) return;

    setIsDeleting(true);
    log.info('Deleting resume', { id });
    await deleteResume(id);
    setIsDeleting(false);

    if (!useResumeStore.getState().error) {
      navigate('/');
    }
  }

  /* -------- render -------- */
  return (
    <div className="editor-page">
      <header className="editor-page__header">
        <Link to="/" className="editor-page__back">
          &larr; Back to Dashboard
        </Link>

        <div className="editor-page__actions">
          {saveMessage && (
            <span className="editor-page__save-msg">{saveMessage}</span>
          )}
          {saveError && (
            <span className="editor-page__save-error">{saveError}</span>
          )}
          <Button
            variant="danger"
            size="md"
            loading={isDeleting}
            onClick={handleDelete}
          >
            Delete
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={isSaving}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </header>

      <LoadingState loading={isLoading && !resume} error={!resume ? storeError : null}>
        {!resume && !isLoading ? (
          <div className="editor-page__not-found">
            <p>Resume not found.</p>
            <Button variant="secondary" size="sm" onClick={() => navigate('/')}>
              Go to Dashboard
            </Button>
          </div>
        ) : (
          <div className="editor-page__body">
            {/* ---------- Title & Summary ---------- */}
            <Panel>
              <Panel.Header>Basic Information</Panel.Header>
              <Panel.Content>
                <div className="editor-field-group">
                  <Input
                    label="Resume Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Senior Frontend Engineer"
                  />
                  <Textarea
                    id="summary"
                    label="Summary"
                    rows={4}
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder="A brief professional summary..."
                  />
                </div>
              </Panel.Content>
            </Panel>

            {/* ---------- Skills ---------- */}
            <Panel>
              <Panel.Header>Skills</Panel.Header>
              <Panel.Content>
                <Input
                  label="Skills (comma-separated)"
                  value={skills}
                  onChange={(e) => setSkills(e.target.value)}
                  placeholder="TypeScript, React, Node.js, PostgreSQL"
                />
              </Panel.Content>
            </Panel>

            {/* ---------- Work Experience ---------- */}
            <Panel>
              <Panel.Header>Work Experience</Panel.Header>
              <Panel.Content>
                {workExperience.length === 0 && (
                  <p className="list-empty">
                    No work experience added yet.
                  </p>
                )}

                {workExperience.map((exp) => (
                  <div key={exp.id} className="experience-entry">
                    <div className="experience-entry__row">
                      <Input
                        label="Company"
                        value={exp.company}
                        onChange={(e) =>
                          updateExperience(exp.id, 'company', e.target.value)
                        }
                        placeholder="Acme Corp"
                      />
                      <Input
                        label="Job Title"
                        value={exp.title}
                        onChange={(e) =>
                          updateExperience(exp.id, 'title', e.target.value)
                        }
                        placeholder="Software Engineer"
                      />
                    </div>
                    <div className="experience-entry__row">
                      <MonthYearPicker
                        label="Start Date"
                        value={exp.startDate}
                        onChange={(v) => {
                          updateExperience(exp.id, 'startDate', v);
                          if (exp.endDate && v && exp.endDate < v) {
                            updateExperience(exp.id, 'endDate', '');
                          }
                        }}
                        placeholder="Select start month"
                      />
                      <MonthYearPicker
                        label="End Date"
                        value={exp.endDate}
                        onChange={(v) => updateExperience(exp.id, 'endDate', v)}
                        placeholder="Select end month"
                        minValue={exp.startDate || undefined}
                      />
                    </div>
                    <Textarea
                      label="Description"
                      rows={3}
                      value={exp.description}
                      onChange={(e) =>
                        updateExperience(exp.id, 'description', e.target.value)
                      }
                      placeholder="Describe your responsibilities and accomplishments..."
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeExperience(exp.id)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}

                <Button variant="secondary" size="sm" onClick={addExperience}>
                  + Add Experience
                </Button>
              </Panel.Content>
            </Panel>
          </div>
        )}
      </LoadingState>
    </div>
  );
}
